import { GetterTree } from 'vuex'
import PartnersState from '../../types/PartnersState'
import RootState from '@vue-storefront/core/types/RootState'
import rootStore from '@vue-storefront/core/store'
import moment from 'moment-timezone'

function filterByTags(partners, activeTag) {
  if (typeof activeTag !== 'undefined') {
    // entity_id arrives as a numeric string (same convention as Category's,
    // see getPartnersByCategory's parseInt below) - partner.tags is a
    // number[], so comparing the raw string here always misses and empties
    // the grid.
    return partners.filter(partner => {
      return partner.tags.includes(parseInt(activeTag.entity_id))
    })
  }
  return partners
}

function calculateDeliveryPrice(group) {
  if (group.partner.shipping.min_free_delivery_price
    && group.partner.shipping.free
    && group.subtotal >= group.partner.shipping.min_free_delivery_price
  ) {
    return 0
  }

  let deliveryPrices = group.partner.shipping.delivery_prices
  if (!deliveryPrices.length) {
    return 70
  }

  if (deliveryPrices.length === 1) {
    return deliveryPrices[0].price
  }

  let p = deliveryPrices
    .sort((a, b) => a.cart_amount - b.cart_amount)
    .find(delivery_price => parseFloat(group.subtotal) <= parseFloat(delivery_price.cart_amount))

  if (typeof p === 'undefined' || typeof p === undefined) {
    let maxCartAmountRule = Math.max.apply(Math, deliveryPrices.map(o => o.cart_amount))
    p = deliveryPrices.find(rule => parseFloat(maxCartAmountRule) === parseFloat(rule.cart_amount))
  }

  return p.price
}

const getters: GetterTree<PartnersState, RootState> = {
  getPartners: state => {
    let activeTag = rootStore.getters['partner_tags/getSelectedTags'][0]
    let partners = state.partners
    if (!partners || !partners.length) {
      return []
    }
    return filterByTags(partners, activeTag)
  },
  getPartnersByCategory: (state, getters) => code => {
    let category = rootStore.getters['partner_categories/getCategories'].find(cat => cat.code === code)
    if (typeof category === 'undefined' || !category) {
      return []
    }
    return getters.getPartnersByLocation.filter(partner => partner.categories.includes(parseInt(category.entity_id)))
  },
  getPartnersByLocation: (state, getters) => {
    let location = rootStore.getters['locations/getActiveLocation']
    if (!location) {
      return []
    }
    // K2-2817: `anywhere` is a real `enabled: true` partner record (so the checkout can
    // resolve it via getPartnerByCode / setShippingMethod), but it's the "deliver
    // anything" pseudo-partner, not a storefront listing - keep it out of every partner
    // grid/category view. Same class of exclusion AboutPartner.vue already does locally.
    return getters.getPartners.filter(partner => partner.location_code === location.code && partner.code !== 'anywhere')
  },
  getPartnerByCode: state => code => {
    return state.partners.find(partner => partner.code === code)
  },
  getPartnerByLocationAndByPartnerCode: state => ({ partnerCode, locationCode }) => {
    return state.partners.find(partner => {
      if (partner.location_code === locationCode) {
        return partner.code === partnerCode
      }
    })
  },
  getCurrentPartnerCode: state => state.current_partner_code,

  getCurrentPartnerDisplayMode: state => {
    let partner
    if (state.current_partner_code) {
      partner = state.partners.find(partner => state.current_partner_code === partner.code)
    }
    return partner && partner.display_mode ? partner.display_mode : false
  },

  getCurrentPartner: state => {
    if (!state.current_partner_code) {
      return false
    }
    // Codes repeat per city once several cities' partners are loaded.
    const location = rootStore.getters['locations/getActiveLocation']
    const matches = state.partners.filter(partner => partner.code === state.current_partner_code)
    return (location && matches.find(partner => partner.location_code === location.code)) || matches[0]
  },
  getListScope: state => state.listScope,

  getGroupedProductsSkus: (state, getters) => {
    let productsInCart = rootStore.state.cart.cartItems
    let currentLocation = rootStore.getters['locations/getActiveLocation']
    let result = []
    if (!currentLocation) {
      return result
    }
    getters.getPartnersByLocation.forEach(partner => {
      if (!partner.sku_prefix) {
        return
      }

      let partnerItems = productsInCart.filter(item => item.sku.startsWith(partner.sku_prefix))
      if (partnerItems.length) {
        result.push(...partnerItems.map(i => i.sku))
      }
    })
    return result
  },
  getGroupedByPartnerProducts: (state, getters) => {
    return rootStore.state.cart.groupedCart;
  },
  isPartnerOpened: state => partnerCode => {
    let partner = state.partners.find(p => p.code === partnerCode)
    return (partner && partner.hasOwnProperty('open')) ? partner.open : false
  },
  isFilterPartnerOpened: state => partnerCode => {
    let partner = state.filterPartners.find(p => p.code === partnerCode)
    return (partner && partner.hasOwnProperty('open')) ? partner.open : false
  },
  canShowFilterPartners: state => state.showFilterPartners,
  getLoader: state => state.partnerLoader,
  getCategorySeoStats: state => state.categorySeoStats

}

export default getters
